import FormBlock from 'components/form/FormBlock';
import { CreatePythonFunctionRequest, Project, PythonFunction } from 'api/model';
import { getFormSchema, getUiSchema, validator } from 'api/python-functions/formSchema';
import { folderExtraErrors, getFolderPathServerError } from 'utils/folderPath';
import usePythonFunction from 'api/python-functions/usePythonFunction';
import usePythonFunctions from 'api/python-functions/usePythonFunctions';
import usePythonFunctionFolders from 'api/python-functions/usePythonFunctionFolders';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Loader from 'components/Loader';
import { Button, CircularProgress, MenuItem, Typography } from '@mui/material';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';
import { RevisionSelect } from 'components/settingsHub/formHeader.styles';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { toast } from 'react-toastify';
import Form from '@rjsf/core';
import { useSearchParams } from 'react-router-dom';
import { FaLink, FaListUl } from 'react-icons/fa6';
import { getNodesUsingFunction } from 'api/python-functions/requests';
import { useNavigate } from 'react-router';
import OutdatedRevisionsToast, { FUNCTION_REVISION_FIELD } from 'views/toasts/OutdatedRevisionsToast';
import useProjects from 'api/projects/useProjects';
import useDialog from 'hooks/useDialog';
import PythonFunctionUsageDialog from 'components/dialog/PythonFunctionUsageDialog';
import PythonFunctionTestRun from './PythonFunctionTestRun';

type Props = {
  functionId: string;
  projectId: string;
  activeWorkflowId?: string;
  inputCount?: number;
  onCancel: () => void;
  onSubmit: (functionId: string) => void;
};

const PythonFunctionEdit = ({ functionId, projectId, activeWorkflowId, inputCount, onCancel, onSubmit }: Props) => {
  const navigate = useNavigate();
  const [showUsageDialog, UsageDialog] = useDialog(PythonFunctionUsageDialog);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRevision = Number(searchParams.get('revision'));
  const {
    pythonFunction: latestFunction,
    update: { mutateAsync: mutateFunction, isPending: isUpdatePending },
    functionIsLoading,
    functionIsFetched,
  } = usePythonFunction({ functionId });
  const { projects, isLoading: areProjectsLoading } = useProjects();
  const { folders } = usePythonFunctionFolders(projectId || null, true);

  const latestRevisionSelected = selectedRevision === latestFunction?.revision;
  const revisionToLoad = selectedRevision || latestFunction?.revision;

  const {
    pythonFunction: revisedFunction,
    functionIsLoading: isRevisedLoading,
    functionIsFetched: isRevisedFetched,
  } = usePythonFunction({
    functionId,
    revision: revisionToLoad,
    pythonFunction: revisionToLoad === latestFunction?.revision ? latestFunction : undefined,
  });

  const pythonFunction = latestRevisionSelected ? latestFunction : revisedFunction;

  const handleChangeRevision = useCallback(
    (revision: number | string) => {
      setSearchParams((params) => {
        params.set('revision', String(revision));
        return params;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (pythonFunction && !selectedRevision) {
      handleChangeRevision(pythonFunction.revision);
    }
  }, [handleChangeRevision, pythonFunction, selectedRevision]);

  const {
    create: { isPending: isSaveCopyPending, mutateAsync: createFunctionCopy },
  } = usePythonFunctions(projectId);

  const handleSave = async (formData: CreatePythonFunctionRequest) => {
    if (!latestFunction) {
      return;
    }
    setFolderError(null);
    try {
      const data = await mutateFunction({ ...formData, revision: latestFunction.revision });
      handleChangeRevision(data.revision);

      if (data.revision <= latestFunction.revision) {
        return;
      }

      const parentNodes = await getNodesUsingFunction(functionId);
      if (parentNodes.length > 0) {
        toast.warning(
          <OutdatedRevisionsToast
            title="The following workflow nodes are not using the latest function revision:"
            nodes={parentNodes}
            revision={data.revision}
            revisionField={() => FUNCTION_REVISION_FIELD}
            activeWorkflowId={activeWorkflowId}
            navigate={navigate}
          />,
          { autoClose: false, closeOnClick: false },
        );
      }
    } catch (e) {
      const serverFolderError = getFolderPathServerError(e);
      if (serverFolderError) {
        setFolderError(serverFolderError);
      } else if (!(e instanceof Error)) {
        toast.error('Error when updating function.');
      }
    }
  };

  const handleSaveAsNew = async (formData: CreatePythonFunctionRequest) => {
    setFolderError(null);
    try {
      const { id: createdId, revision } = await createFunctionCopy({
        ...formData,
        name: formData.name === pythonFunction?.name ? `${formData.name}(copy)` : formData.name,
      });
      handleChangeRevision(revision);
      onSubmit(createdId);
    } catch (e) {
      const serverFolderError = getFolderPathServerError(e);
      if (serverFolderError) {
        setFolderError(serverFolderError);
      } else if (e instanceof Error) {
        toast.error(`Error when creating function copy: ${e.message}`);
      } else {
        toast.error('An unknown error occurred.');
      }
    }
  };

  const revisionOptions = Array.from({ length: latestFunction?.revision ?? 0 }, (_, i) => i + 1).reverse();

  const handleCopyLink = () => {
    const url = window.location.href.includes('functionId=')
      ? window.location.href
      : `${window.location.href.split('?', 1)[0]}?config=python-functions&functionId=${pythonFunction?.id}&revision=${pythonFunction?.revision}`;

    void navigator.clipboard.writeText(url).then(() => {
      toast.success('Function link copied to clipboard');
    });
  };

  const handleClose = useCallback(() => {
    setSearchParams({});
    onCancel();
  }, [setSearchParams, onCancel]);

  useEffect(() => {
    if (!pythonFunction && functionIsFetched && isRevisedFetched) {
      toast.error('Python function not found!');
      handleClose();
    }
  }, [pythonFunction, functionIsFetched, isRevisedFetched, handleClose]);

  if (functionIsLoading || isRevisedLoading || areProjectsLoading) {
    return <Loader />;
  }

  const headerActions = (
    <div className="flex items-center gap-2">
      <ActionIconButton size="small" color="info" title="View usage" onClick={() => showUsageDialog(true)}>
        <FaListUl size={16} />
      </ActionIconButton>
      <ActionIconButton size="small" color="info" title="Copy link" onClick={handleCopyLink}>
        <FaLink size={16} />
      </ActionIconButton>
      <Typography component="span" variant="caption" sx={{ color: 'info.main' }}>
        revision:
      </Typography>
      <RevisionSelect
        key={selectedRevision}
        size="small"
        value={selectedRevision}
        onChange={(event) => handleChangeRevision(event.target.value as number)}
        aria-label="revision"
      >
        {revisionOptions.map((revision: number) => (
          <MenuItem key={revision} value={revision}>
            {revision}
          </MenuItem>
        ))}
      </RevisionSelect>
    </div>
  );

  return (
    <>
      <PythonFunctionFormEditor
        key={`form-${pythonFunction?.id}-${pythonFunction?.revision}`}
        title="Edit Python Function"
        headerActions={headerActions}
        pythonFunction={pythonFunction}
        projects={projects}
        folders={folders}
        inputCount={inputCount}
        isUpdatePending={isUpdatePending}
        isSaveCopyPending={isSaveCopyPending}
        latestRevisionSelected={latestRevisionSelected}
        folderError={folderError}
        onClose={handleClose}
        onSave={(formData) => void handleSave(formData)}
        onSaveAsNew={(formData) => void handleSaveAsNew(formData)}
      />
      <UsageDialog pythonFunction={pythonFunction} onClose={() => showUsageDialog(false)} />
    </>
  );
};

type PythonFunctionFormEditorProps = {
  title: string;
  headerActions: ReactNode;
  pythonFunction?: PythonFunction;
  projects?: Project[];
  folders: string[];
  inputCount?: number;
  isUpdatePending: boolean;
  isSaveCopyPending: boolean;
  latestRevisionSelected: boolean;
  folderError: string | null;
  onClose: () => void;
  onSave: (formData: CreatePythonFunctionRequest) => void;
  onSaveAsNew: (formData: CreatePythonFunctionRequest) => void;
};

const PythonFunctionFormEditor = ({
  title,
  headerActions,
  pythonFunction,
  projects,
  folders,
  inputCount,
  isUpdatePending,
  isSaveCopyPending,
  latestRevisionSelected,
  folderError,
  onClose,
  onSave,
  onSaveAsNew,
}: PythonFunctionFormEditorProps) => {
  const formRef = useRef<Form<CreatePythonFunctionRequest>>(null);
  const [formData, setFormData] = useState<CreatePythonFunctionRequest | null>(null);

  const pending = isUpdatePending || isSaveCopyPending;

  const validateForm = () => {
    if (!formRef.current?.validateForm()) {
      toast.error('Form validation failed. Please check the fields.');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (formData && validateForm()) {
      onSave(formData);
    }
  };

  const handleSaveAsNew = () => {
    if (formData && validateForm()) {
      onSaveAsNew(formData);
    }
  };

  return (
    <SettingsFormShell
      title={title}
      headerActions={headerActions}
      footer={
        <>
          <Button disabled={pending} variant="outlined" onClick={onClose} className="mr-auto">
            close
          </Button>
          <Button disabled={!formData || pending} variant="contained" className="relative" onClick={handleSaveAsNew}>
            {isSaveCopyPending ? <CircularProgress size="20" /> : 'Save as new'}
          </Button>
          {latestRevisionSelected && (
            <Button disabled={!formData || pending} variant="contained" onClick={handleSave} className="relative">
              {isUpdatePending && <CircularProgress className="absolute left-1/2 top-1/2" size="20" />}
              Save
            </Button>
          )}
        </>
      }
    >
      <FormBlock
        initialValues={pythonFunction}
        onSubmit={() => undefined}
        onChange={setFormData}
        onCancel={onClose}
        schema={getFormSchema(projects, undefined)}
        uiSchema={getUiSchema(folders)}
        showButtons={false}
        disabled={pending}
        ref={formRef}
        validator={validator}
        extraErrors={folderExtraErrors(folderError)}
      />
      <PythonFunctionTestRun
        functionCode={formData?.function_code ?? pythonFunction?.function_code}
        extraLibs={formData?.extra_libs ?? pythonFunction?.extra_libs}
        inputCount={inputCount}
        disabled={pending}
      />
    </SettingsFormShell>
  );
};

export default PythonFunctionEdit;
